import React, { useEffect, useMemo, useState } from "react"
import {
    Container,
    Row,
    Col,
    Table,
    Card,
    CardBody,
    Modal,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import NewPagination from "components/Common/Newpagination"
import { useGlobalFilter, usePagination, useTable } from "react-table"
import { useNavigate, useParams } from "react-router-dom"
import { fetchNodeData } from "utils/common-fetchData"
import secureLocalStorage from "react-secure-storage"
import Loader from "pages/Separate/Loader"

// answers is the enriched array from API:
// [{ question_id, type, user_answer, correct_answer, isCorrect, question }]
// DB type: 1 = MCQ, 2 = Text, 3 = Code
function calcScore(answers = []) {
    const mcq = answers.filter(a => a.type === 1)
    const correct = mcq.filter(a => a.isCorrect === true).length
    const total = answers.length
    const pct = mcq.length > 0 ? Math.round((correct / mcq.length) * 100) : 0
    return { correct, total, autoGradable: mcq.length, pct }
}

function ScoreBadge({ pct }) {
    const color = pct >= 75 ? "#27ae60" : pct >= 50 ? "#f39c12" : "#e74c3c"
    const bg = pct >= 75 ? "#e8f5e9" : pct >= 50 ? "#fff8e1" : "#fdecea"
    return (
        <span style={{
            background: bg,
            color,
            border: `1.5px solid ${color}`,
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 13,
            fontWeight: 700,
        }}>
            {pct}%
        </span>
    )
}

const VIO_META = {
    tab_switch: { icon: "fas fa-exchange-alt", color: "#e67e22", bg: "#fff8e1", border: "#ffc107" },
    window_blur: { icon: "fas fa-desktop", color: "#e74c3c", bg: "#fdecea", border: "#ef9a9a" },
    fullscreen_exit: { icon: "fas fa-compress-alt", color: "#8e44ad", bg: "#f3e5f5", border: "#ce93d8" },
}

function ViolationsModal({ isOpen, onClose, violations = [], candidateName }) {
    const leaveCount = violations.filter(v => v.kind === "leave" || !v.kind).length

    return (
        <Modal isOpen={isOpen} size="md" centered toggle={onClose}>
            <div className="modal-header px-4">
                <h5 className="modal-title mt-0">
                    <i className="fas fa-shield-alt me-2" style={{ color: "#e74c3c" }}></i>
                    Proctoring Activity
                    {candidateName && <span className="ms-2 fw-normal" style={{ fontSize: 14, color: "#666" }}>— {candidateName}</span>}
                </h5>
                <button type="button" onClick={onClose} className="btn-modal-close">
                    <span aria-hidden="true" className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body px-4 py-3">
                {violations.length === 0 ? (
                    <div className="text-center py-4">
                        <i className="fas fa-check-circle" style={{ fontSize: 36, color: "#27ae60" }}></i>
                        <p className="mt-2 mb-0" style={{ color: "#555" }}>No violations recorded for this session.</p>
                    </div>
                ) : (
                    <>
                        <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                            <span className="badge" style={{ background: "#e74c3c", fontSize: 12 }}>
                                {leaveCount} violation{leaveCount !== 1 ? "s" : ""}
                            </span>
                            <span style={{ fontSize: 11, color: "#aaa" }}>
                                <i className="fas fa-info-circle me-1"></i>
                                App name detection is not available in browsers
                            </span>
                        </div>

                        {/* Timeline */}
                        <div style={{ position: "relative", paddingLeft: 24 }}>
                            {/* vertical line */}
                            <div style={{ position: "absolute", left: 8, top: 8, bottom: 8, width: 2, background: "#e9ecef", borderRadius: 2 }} />

                            {violations.map((v, i) => {
                                const isResume = v.kind === "resume" || v.type === "exam_resumed"
                                const meta = isResume
                                    ? { icon: "fas fa-play-circle", color: "#27ae60", bg: "#e8f5e9", border: "#a5d6a7" }
                                    : VIO_META[v.type] || { icon: "fas fa-exclamation-triangle", color: "#555", bg: "#f5f5f5", border: "#ccc" }

                                return (
                                    <div key={i} style={{ position: "relative", marginBottom: 10 }}>
                                        {/* dot on timeline */}
                                        <div style={{
                                            position: "absolute", left: -20, top: "50%", transform: "translateY(-50%)",
                                            width: 12, height: 12, borderRadius: "50%",
                                            background: meta.color, border: "2px solid #fff",
                                            boxShadow: `0 0 0 2px ${meta.color}`,
                                        }} />
                                        <div className="d-flex align-items-center gap-2 rounded px-3 py-2"
                                            style={{ background: meta.bg, border: `1.5px solid ${meta.border}` }}>
                                            <i className={meta.icon} style={{ color: meta.color, fontSize: 14, minWidth: 16 }}></i>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: meta.color }}>
                                                    {v.label}
                                                    {isResume && v.durationAway != null && (
                                                        <span style={{ fontSize: 11, fontWeight: 400, color: "#666", marginLeft: 6 }}>
                                                            (away {v.durationAway}s)
                                                        </span>
                                                    )}
                                                </div>
                                                {!isResume && (
                                                    <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>
                                                        {v.type === "tab_switch" && "Switched to another browser tab"}
                                                        {v.type === "window_blur" && "Switched to another application or OS window"}
                                                        {v.type === "fullscreen_exit" && "Exited fullscreen mode"}
                                                    </div>
                                                )}
                                            </div>
                                            <span style={{ fontSize: 11, color: "#888", whiteSpace: "nowrap" }}>
                                                <i className="fas fa-clock me-1"></i>{v.time}
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    )
}

function DetailModal({ isOpen, onClose, response }) {
    if (!response) return null
    const score = calcScore(response.answers)
    const violationCount = (response.violations || []).length

    return (
        <Modal isOpen={isOpen} size="lg" centered toggle={onClose}>
            <div className="modal-header px-4">
                <h5 className="modal-title mt-0">
                    Response Detail — <b>{response.name}</b>
                </h5>
                <button type="button" onClick={onClose} className="btn-modal-close">
                    <span aria-hidden="true" className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body px-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                {/* Summary row */}
                <div className="d-flex gap-3 mb-4 flex-wrap">
                    <div className="border rounded p-3 text-center" style={{ minWidth: 100 }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#27ae60" }}>{score.correct}</div>
                        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>Correct (MCQ)</div>
                    </div>
                    <div className="border rounded p-3 text-center" style={{ minWidth: 100 }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#e74c3c" }}>{score.autoGradable - score.correct}</div>
                        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>Wrong (MCQ)</div>
                    </div>
                    <div className="border rounded p-3 text-center" style={{ minWidth: 100 }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: "#752a97" }}>{score.total}</div>
                        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>Total Questions</div>
                    </div>
                    <div className="border rounded p-3 text-center" style={{ minWidth: 100 }}>
                        <ScoreBadge pct={score.pct} />
                        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", marginTop: 4 }}>MCQ Score</div>
                    </div>
                    <div className="border rounded p-3 text-center" style={{ minWidth: 100, borderColor: violationCount > 0 ? "#ef9a9a" : "#a5d6a7", background: violationCount > 0 ? "#fdecea" : "#e8f5e9" }}>
                        <div style={{ fontSize: 24, fontWeight: 800, color: violationCount > 0 ? "#c62828" : "#2e7d32" }}>{violationCount}</div>
                        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase" }}>Violations</div>
                    </div>
                </div>

                <div style={{ borderTop: "1px solid #eee", marginBottom: 16, paddingTop: 16 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: "#333" }}>
                        <i className="fas fa-list me-2" style={{ color: "#752a97" }}></i>Question Responses
                    </span>
                </div>

                {/* Question-by-question */}
                {(response.answers || []).map((ans, i) => {
                    const isMcq = ans.type === 1
                    const isCode = ans.type === 3
                    const isCorrect = ans.isCorrect
                    const qText = ans.question?.question || `Question ${ans.question_id}`

                    // Parse MCQ options to resolve index → text
                    let optionMap = {}
                    if (isMcq && ans.question?.options) {
                        try { optionMap = JSON.parse(ans.question.options) } catch { }
                    }
                    const userOptionText = isMcq
                        ? (optionMap[ans.user_answer]
                            ? `Option ${ans.user_answer} — ${optionMap[ans.user_answer]}`
                            : ans.user_answer || "No answer")
                        : null
                    const correctOptionText = isMcq && ans.correct_answer
                        ? `Option ${ans.correct_answer} — ${optionMap[ans.correct_answer] ?? ans.correct_answer}`
                        : null

                    const borderColor = isCorrect === true ? "#27ae60" : isCorrect === false ? "#e74c3c" : "#752a97"
                    return (
                        <div key={ans.question_id ?? i} className="mb-3 border rounded p-3"
                            style={{ borderLeft: `4px solid ${borderColor}` }}>
                            <div className="d-flex align-items-start gap-2 mb-2">
                                <span className="badge" style={{ background: "#752a97", fontSize: 12 }}>Q{i + 1}</span>
                                <span className="badge" style={{ background: isMcq ? "#0d6efd" : isCode ? "#6f42c1" : "#6c757d", fontSize: 11 }}>
                                    {isMcq ? "MCQ" : isCode ? "Code" : "Text"}
                                </span>
                                <span style={{ fontSize: 13, fontWeight: 600, color: "#333" }}>{qText}</span>
                                {isCorrect === true && <span className="ms-auto badge bg-success">Correct</span>}
                                {isCorrect === false && <span className="ms-auto badge bg-danger">Wrong</span>}
                                {isCorrect === null && <span className="ms-auto badge bg-secondary">Manual Review</span>}
                            </div>

                            {/* User's answer */}
                            {isMcq ? (
                                <div className="mb-1" style={{ fontSize: 13 }}>
                                    <span style={{ color: "#666" }}>Selected: </span>
                                    <b style={{ color: isCorrect === true ? "#27ae60" : isCorrect === false ? "#e74c3c" : "#333" }}>
                                        {userOptionText}
                                    </b>
                                </div>
                            ) : (
                                ans.user_answer ? (
                                    <pre style={{ background: isCode ? "#1e1e2e" : "#f8f8f8", color: isCode ? "#cdd6f4" : "#333", borderRadius: 6, padding: "10px 12px", fontSize: 13, fontFamily: isCode ? "monospace" : "inherit", whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
                                        {ans.user_answer}
                                    </pre>
                                ) : (
                                    <span style={{ color: "#aaa", fontSize: 13, fontStyle: "italic" }}>No answer provided</span>
                                )
                            )}

                            {/* Correct answer (MCQ only) */}
                            {correctOptionText && (
                                <div style={{ marginTop: 6, fontSize: 12, color: "#27ae60" }}>
                                    <i className="fas fa-check-circle me-1"></i>
                                    Correct: <b>{correctOptionText}</b>
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </Modal>
    )
}

function TableContainer({ columns, data }) {
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        state,
        previousPage,
        nextPage,
        canPreviousPage,
        canNextPage,
        gotoPage,
        setPageSize,
        pageOptions,
        setGlobalFilter,
        prepareRow,
    } = useTable({ columns, data }, useGlobalFilter, usePagination)

    const { pageIndex, pageSize, globalFilter } = state
    const navigate = useNavigate()

    return (
        <div className="px-2">
            <Row className="mb-2">
                <Col lg={2}>
                    <select
                        className="border-radius form-select"
                        style={{ width: "100px" }}
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 30, 40, 50].map(size => (
                            <option key={size} value={size}>Show {size}</option>
                        ))}
                    </select>
                </Col>
                <Col className="d-flex justify-content-end">
                    <div className="me-4">
                        <div className="search-box d-inline-block">
                            <div className="position-relative">
                                <input
                                    type="text"
                                    className="border-radius form-control"
                                    placeholder="Search..."
                                    value={globalFilter || ""}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                />
                                <i className="bx bx-search-alt search-icon"></i>
                            </div>
                        </div>
                    </div>
                    <button
                        className="border-radius btn text-white ms-md-2 btn-red"
                        onClick={() => navigate(-1)}
                    >
                        <i className="dripicons-cross me-2 icon-center"></i>Back
                    </button>
                </Col>
            </Row>

            <Table className="table table-bordered mt-4" {...getTableProps()}>
                <thead>
                    {headerGroups.map((headerGroup, i) => (
                        <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                            {headerGroup.headers.map((column, index) => (
                                <th key={index} className={column.className} {...column.getHeaderProps()}>
                                    {column.render("Header")}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)
                        return (
                            <tr key={i} {...row.getRowProps()}>
                                {row.cells.map((cell, index) => (
                                    <td key={index} className={cell.column.className} {...cell.getCellProps()}>
                                        {cell.render("Cell")}
                                    </td>
                                ))}
                            </tr>
                        )
                    })}
                </tbody>
            </Table>

            <Row className="justify-content-md-end justify-content-center align-items-center">
                <NewPagination
                    gotoPage={gotoPage}
                    canPreviousPage={canPreviousPage}
                    previousPage={previousPage}
                    canNextPage={canNextPage}
                    pageIndex={pageIndex}
                    pageOptions={pageOptions}
                    pageSize={pageSize}
                    page={page}
                    data={data}
                    nextPage={nextPage}
                />
            </Row>
        </div>
    )
}

const AssessmentResponsesIndex = () => {
    const { testId } = useParams()
    const token = secureLocalStorage.getItem("auth_token")
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const [detailModal, setDetailModal] = useState(false)
    const [selectedResponse, setSelectedResponse] = useState(null)
    const [violationsModal, setViolationsModal] = useState(false)
    const [selectedViolations, setSelectedViolations] = useState({ list: [], name: "" })

    useEffect(() => {
        if (!testId) { setLoading(false); return }
        fetchNodeData(setLoading, `preassesment/data/getResponse/${testId}`, token)
            .then(res => {
                if (res?.success) setData(res.data || [])
            })
    }, [testId])

    const openDetail = row => {
        setSelectedResponse(row)
        setDetailModal(true)
    }

    const openViolations = row => {
        setSelectedViolations({ list: row.violations || [], name: row.name })
        setViolationsModal(true)
    }

    const columns = useMemo(
        () => [
            {
                Header: "#",
                accessor: "id",
                className: "text-center",
            },
            {
                Header: "Candidate",
                accessor: "name",
                className: "",
            },
            {
                Header: "Email",
                accessor: "email",
                className: "",
            },
            {
                Header: "Date",
                accessor: "created_at",
                className: "text-center",
                Cell: ({ value }) => value ? new Date(value).toLocaleDateString("en-IN") + " " + new Date(value).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-",
            },
            {
                Header: "Score",
                accessor: "score",
                className: "text-center",
                Cell: ({ row }) => {
                    const score = calcScore(row.original.answers || [])
                    return (
                        <div className="d-flex align-items-center justify-content-center gap-2">
                            <ScoreBadge pct={score.pct} />
                            <span style={{ fontSize: 12, color: "#888" }}>
                                {score.correct}/{score.autoGradable} MCQ
                            </span>
                        </div>
                    )
                },
            },
            {
                Header: "Answered",
                accessor: "answered",
                className: "text-center",
                Cell: ({ row }) => {
                    const answers = row.original.answers || []
                    const answered = answers.filter(a => a.user_answer && a.user_answer.toString().trim()).length
                    return (
                        <span style={{ fontWeight: 600 }}>
                            {answered} / {answers.length}
                        </span>
                    )
                },
            },
            {
                Header: "Violations",
                accessor: "violation_count",
                className: "text-center",
                Cell: ({ row }) => {

                    const count = row.original.violations.filter(v => v.kind === "leave").length || 0
                    if (count === 0)
                        return (
                            <span style={{ background: "#e8f5e9", color: "#2e7d32", border: "1.5px solid #a5d6a7", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
                                <i className="fas fa-check me-1"></i>Clean
                            </span>
                        )
                    return (
                        <span
                            onClick={() => openViolations(row.original)}
                            style={{ background: "#fdecea", color: "#c62828", border: "1.5px solid #ef9a9a", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                            title="Click to view violations"
                        >
                            <i className="fas fa-exclamation-triangle me-1"></i>{count}
                        </span>
                    )
                },
            },
            {
                Header: "Action",
                accessor: "action",
                className: "text-center",
                Cell: ({ row }) => (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => openDetail(row.original)}
                    >
                        <i className="fas fa-eye me-1"></i>Result
                    </button>
                ),
            },
        ],
        [openViolations]
    )

    if (loading) return <Loader />

    return (
        <React.Fragment>
            <div className="page-content mb-4">
                <Container fluid>
                    <div className="px-2">
                        <Breadcrumbs title="Assessment" breadcrumbItem="Responses" />
                    </div>
                    <Card>
                        <CardBody>
                            <div className="responsive-table">
                                <TableContainer columns={columns} data={data} />
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <DetailModal
                isOpen={detailModal}
                onClose={() => setDetailModal(false)}
                response={selectedResponse}
            />
            <ViolationsModal
                isOpen={violationsModal}
                onClose={() => setViolationsModal(false)}
                violations={selectedViolations.list}
                candidateName={selectedViolations.name}
            />
        </React.Fragment>
    )
}

export default AssessmentResponsesIndex
